import React, { useState, useEffect } from 'react';
import { Property } from '../types';

// Add this mapping function right here
const mapAddressData = (addressData: any) => {
  console.log('🔄 Raw address data for mapping:', addressData);
  console.log('🔑 Available keys in address data:', Object.keys(addressData));
  
  // Use the exact field names from your backend
  const mapped = {
    addressLine1: addressData.street || '', // This is 'street' from backend
    addressLine2: addressData.address_line_2 || '', // Optional field
    suburb: addressData.state || '', // Suburb is 'state' from backend (region in DB)
    city: addressData.city || '',
    postalCode: addressData.zipCode || '', // This is 'zipCode' from backend
    region: addressData.province || '', // Region is 'province' from backend
    country: addressData.country || 'South Africa' // Default to South Africa
  };

  return mapped;
};

interface EditablePropertyCardProps {
  property: Property;
  index: number;
  onSave: (index: number, updatedData: Property) => void;
  onCancel: (index: number) => void;
  onEdit: (index: number) => void;
  cardNumber?: number;
  cardTitle?: string;
  showCardNumber?: boolean;
}

// Define Property interface

interface PropertyErrors {
  addressLine1: string;
  suburb: string;
  city: string;
  postalCode: string;
  region: string;
  country: string;
  propertyType: string;
  residenceType: string;
  buildingAge: string;
  situatedAt: string;
  coverType: string;
  insuredValue: string;
  server: string;
  wallMaterials?: string;
  roofMaterials?: string;
  floorMaterials?: string;
  otherSurroundingAreas?: string;
  premium?: string;
  excess?: string;
  noClaimBonus?: string;
}

interface AddPropertyModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (propertyData: Property) => void;
  clientId: number;
  property?: Property;
  mode?: 'create' | 'edit' | 'view';
  onEdit?: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || '';

const propertyTypes = ['House', 'Townhouse', 'Cottage (Attached)', 'Cottage (Not Attached)', 'Cluster', 'Flat Above Ground', 'Flat On Ground', 'Outbuilding', 'Storage Facility', 'Park Home/Caravan', 'Plot', 'Established', 'New Extension', 'Farm', 'Informal Settlement', 'Other'];
const residenceTypes = ['Primary Residence', 'Business Residence', 'Holiday Residence', 'Other'];
const situatedAtOptions = ['Plot', 'New Extension', 'Farm', 'Established', 'InformalSettlement', 'Rural Area', 'Suburb', 'Other'];
const coverTypeOptions = ['Comprehensive', 'Theft Cover Excluding', 'Flood Cover Excluding', 'Advensure'];
const buildingAgeOptions = ['0-5 years', '6-10 years', '11-25 years', 'Older than 25 years'];
const wallMaterialsOptions = ['Brick', 'Cement', 'Corrugated Iron', 'Glass', 'Prefab', 'Asbestos', 'Wood', 'Other'];
const roofMaterialsOptions = ['Concrete', 'Corrugated Iron', 'Glass', 'Thatch', 'Asbestos', 'Wood', 'Tiles'];
const floorMaterialsOptions = ['Carpet', 'Sealed', 'Tiles', 'Wooden Floors'];
const surroundingAreasOptions = ['Farm', 'Plot', 'Golf Estate', 'Country Estate/Residential Area', 'Security Complex', 'Complex With No Access Control', 'Boomed Off Suburb', 'Suburb', 'Industrial Area', 'Commercial Area', 'Inner City/CBD', 'Informal Settlement', 'Retirement Village', 'Nature Reserve', 'Holiday Resort', 'School/University', 'Parks', 'Highways', 'Railway Lines', 'Shops/Cafes', 'Squatter Camps', 'Open Fields', 'Other'];
const noClaimBonusOptions = ['12 Months  (1 Year)', '24 Months  (2 Year)', '36 Months  (3 Year)', '48 Months  (4 Year)', '60 Months  (5 Year)', '72 Months  (6 Year)', '84 Months  (7 Year)', '96 Months  (8 Year)', '108 Months  (9 Year)', '120 Months  (10 Year)'];

// Currency formatting helper functions
const formatCurrencyInput = (value: string | number): string => {
  if (!value && value !== 0) return '';
  
  const stringValue = typeof value === 'number' ? value.toString() : value;
  const numericValue = stringValue.replace(/[^\d.]/g, '');
  
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  
  if (decimalPart.length > 2) {
    decimalPart = decimalPart.substring(0, 2);
  }
  
  if (wholePart) {
    wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
  }
  
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
};

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/,/g, ''));
};

const AddPropertyModal: React.FC<AddPropertyModalProps> = ({
  isVisible,
  onClose,
  onSave,
  clientId,
  property,
  mode = 'create',
  onEdit,
}) => {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

const cleanMaterialArray = (data: any): string[] => {
  if (!data) return [];
  
  if (Array.isArray(data)) {
    return data.map(item => {
      if (typeof item === 'string') {
        let cleaned = item.trim();
        // Remove JSON artifacts
        cleaned = cleaned.replace(/^\{"/, '');
        cleaned = cleaned.replace(/"\}$/, '');
        cleaned = cleaned.replace(/^"/, '');
        cleaned = cleaned.replace(/"$/, '');
        cleaned = cleaned.replace(/\\"/g, '"');
        
        // Try to parse if it looks like JSON
        if (cleaned.includes('{') && cleaned.includes('}')) {
          try {
            const parsed = JSON.parse(cleaned);
            return parsed.value || parsed.name || cleaned;
          } catch {
            return cleaned;
          }
        }
        return cleaned;
      }
      return String(item);
    }).filter(Boolean);
  }
  
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) {
        return parsed.map(item => String(item).trim()).filter(Boolean);
      }
    } catch {
      return data.split(',').map((item: string) => item.trim()).filter(Boolean);
    }
  }
  
  return [];
};

// function to initialize currency display from numeric values
const initializeCurrencyDisplay = (propertyData?: Property) => {
  return {
    insuredValue: formatCurrencyInput(propertyData?.insuredValue?.toString() || ''),
    premium: formatCurrencyInput(propertyData?.premium?.toString() || ''),
    excess: formatCurrencyInput(propertyData?.excess?.toString() || ''),
    powerSurgeCoverValue: formatCurrencyInput(propertyData?.powerSurgeCoverValue?.toString() || ''),
    accidentalDamageCoverValue: formatCurrencyInput(propertyData?.accidentalDamageCoverValue?.toString() || ''),
  };
};

  // Initialize form data based on mode
  const getInitialFormData = (): Partial<Property> => {
    if (isEditMode || isViewMode) {

      // Add debug logging for thatch fields
      console.log('🔍 getInitialFormData - Loading property data:', {
        propertyId: property?.id,
        hasThatchRoof: property?.hasThatchRoof,
        thatchLessThan5m: property?.thatchLessThan5m,
        thatchSabsConductor: property?.thatchSabsConductor,
        thatchTreated: property?.thatchTreated,
        rawTypes: {
          hasThatchRoof: typeof property?.hasThatchRoof,
          thatchLessThan5m: typeof property?.thatchLessThan5m,
          thatchSabsConductor: typeof property?.thatchSabsConductor,
          thatchTreated: typeof property?.thatchTreated,
        }
      });
      
      // Use the cleanMaterialArray function to properly clean and parse the data
      const cleanedWallMaterials = cleanMaterialArray(property?.wallMaterials);
      const cleanedRoofMaterials = cleanMaterialArray(property?.roofMaterials);
      const cleanedFloorMaterials = cleanMaterialArray(property?.floorMaterials);
      const cleanedOtherSurroundingAreas = cleanMaterialArray(property?.otherSurroundingAreas);
      
      const formData = {
        addressLine1: property?.addressLine1 || '',
        addressLine2: property?.addressLine2 || '',
        suburb: property?.suburb || '',
        city: property?.city || '',
        postalCode: property?.postalCode || '',
        region: property?.region || '',
        country: property?.country || '',
        propertyType: property?.propertyType || '',
        residenceType: property?.residenceType || '',
        buildingAge: property?.buildingAge || '',
        situatedAt: property?.situatedAt || '',
        coverType: property?.coverType || '',
        insuredValue: property?.insuredValue || 0,
        hasSwimmingPool: property?.hasSwimmingPool || false,
        hasMotorizedGate: property?.hasMotorizedGate || false,
        isUnoccupiedMoreThan60Days: property?.isUnoccupiedMoreThan60Days || false,
        isCommune: property?.isCommune || false,
        underConstruction: property?.underConstruction || false,
        builtOnSteepSlope: property?.builtOnSteepSlope || false,
        hasExcessiveRetainingWalls: property?.hasExcessiveRetainingWalls || false,
        nearWaterBody: property?.nearWaterBody || false,
        hasRisingDamp: property?.hasRisingDamp || false,
        hasWallCracks: property?.hasWallCracks || false,
        hasSubsidenceHistory: property?.hasSubsidenceHistory || false,
        hasOutbuilding: property?.hasOutbuilding || false,
        hasPerimeterFence: property?.hasPerimeterFence || false,
        hasElectricFence: property?.hasElectricFence || false,
        hasThatchRoof: property?.hasThatchRoof || false,
        thatchLessThan5m: property?.thatchLessThan5m || false,
        thatchSabsConductor: property?.thatchSabsConductor || false,
        thatchTreated: property?.thatchTreated || false,
        hasAccidentalDamageCover: property?.hasAccidentalDamageCover || false,
        accidentalDamageCoverValue: property?.accidentalDamageCoverValue || 0,
        hasPowerSurgeCover: property?.hasPowerSurgeCover || false,
        powerSurgeCoverValue: property?.powerSurgeCoverValue || 0,
        // Use the cleaned arrays
        wallMaterials: cleanedWallMaterials,
        roofMaterials: cleanedRoofMaterials,
        floorMaterials: cleanedFloorMaterials,
        otherSurroundingAreas: cleanedOtherSurroundingAreas,
        premium: property?.premium || '',
        excess: property?.excess || 0,
        noClaimBonus: property?.noClaimBonus || '',
      };
      
      return formData;
    }
    
    // For create mode, use empty defaults
    return {
      addressLine1: '',
      addressLine2: '',
      suburb: '',
      city: '',
      postalCode: '',
      region: '',
      country: 'South Africa',
      propertyType: '',
      residenceType: '',
      buildingAge: '',
      situatedAt: '',
      coverType: '',
      insuredValue: 0,
      hasSwimmingPool: false,
      hasMotorizedGate: false,
      isUnoccupiedMoreThan60Days: false,
      isCommune: false,
      underConstruction: false,
      builtOnSteepSlope: false,
      hasExcessiveRetainingWalls: false,
      nearWaterBody: false,
      hasRisingDamp: false,
      hasWallCracks: false,
      hasSubsidenceHistory: false,
      hasOutbuilding: false,
      hasPerimeterFence: false,
      hasElectricFence: false,
      hasThatchRoof: false,
      thatchLessThan5m: false,
      thatchSabsConductor: false,
      thatchTreated: false,
      hasAccidentalDamageCover: false,
      accidentalDamageCoverValue: 0,
      hasPowerSurgeCover: false,
      powerSurgeCoverValue: 0,
      wallMaterials: [],
      roofMaterials: [],
      floorMaterials: [],
      otherSurroundingAreas: [],
      premium: '',
      excess: 0,
      noClaimBonus: '',
    };
  };

  const [propertyFormData, setPropertyFormData] = useState<Partial<Property>>(getInitialFormData);
  const [currencyDisplay, setCurrencyDisplay] = useState({
    insuredValue: '',
    premium: '',
    excess: '',
    powerSurgeCoverValue: '',
    accidentalDamageCoverValue: '',
  });
  const [propertyErrors, setPropertyErrors] = useState<PropertyErrors>({
    addressLine1: '',
    suburb: '',
    city: '',
    postalCode: '',
    region: '',
    country: '',
    propertyType: '',
    residenceType: '',
    buildingAge: '',
    situatedAt: '',
    coverType: '',
    insuredValue: '',
    server: '',
    wallMaterials: '',
    roofMaterials: '',
    floorMaterials: '',
    otherSurroundingAreas: '',
    premium: '',
    excess: '',
    noClaimBonus: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Add this function inside AddPropertyModal
const clearAllSelections = (field: 'wallMaterials' | 'roofMaterials' | 'floorMaterials' | 'otherSurroundingAreas') => {
  setPropertyFormData(prev => ({
    ...prev,
    [field]: []
  }));
};

const handleCheckboxGroupChange = (
  field: 'wallMaterials' | 'roofMaterials' | 'floorMaterials' | 'otherSurroundingAreas',
  option: string,
  isChecked: boolean
) => {
  setPropertyFormData(prev => {
    const currentArray = (prev[field] as string[]) || [];
    
    // Clean the option string for comparison
    const cleanedOption = cleanMaterialArray([option])[0] || option;
    
    // Check if option is already in the array (with cleaning)
    const optionExists = currentArray.some(item => {
      const cleanedItem = cleanMaterialArray([item])[0] || item;
      return cleanedItem === cleanedOption;
    });
    
    if (isChecked && !optionExists) {
      // Add option if checked and not already in array
      return {
        ...prev,
        [field]: [...currentArray, option] // Use the original option
      };
    } else if (!isChecked && optionExists) {
      // Remove option if unchecked (find by cleaned comparison)
      const indexToRemove = currentArray.findIndex(item => {
        const cleanedItem = cleanMaterialArray([item])[0] || item;
        return cleanedItem === cleanedOption;
      });
      
      if (indexToRemove !== -1) {
        const newArray = [...currentArray];
        newArray.splice(indexToRemove, 1);
        return {
          ...prev,
          [field]: newArray
        };
      }
    }
    
    return prev;
  });
  
  setPropertyErrors(prev => ({ ...prev, [field]: '' }));
};

  // Reset form when mode or property changes
  useEffect(() => {
    if (isVisible) {
      
      // Log each material option with its checked status
      const logMaterialStatus = (fieldName: string, options: string[], savedData: string[]) => {
        options.forEach(option => {
          const isInSavedData = savedData?.includes(option) || false;
        });
      };
      
      const initialData = getInitialFormData();
      
      // Log the status for each material
      logMaterialStatus('Wall Materials', wallMaterialsOptions, initialData.wallMaterials as string[]);
      logMaterialStatus('Roof Materials', roofMaterialsOptions, initialData.roofMaterials as string[]);
      logMaterialStatus('Floor Materials', floorMaterialsOptions, initialData.floorMaterials as string[]);
      logMaterialStatus('Other Surrounding Areas', surroundingAreasOptions, initialData.otherSurroundingAreas as string[]);
    }
  }, [property, isVisible, mode]);

  // Initialize currency display when property changes
  useEffect(() => {
    if (property && (isEditMode || isViewMode) && isVisible) {
      
      // Initialize currency display from property values
      setCurrencyDisplay({
        insuredValue: formatCurrencyInput(property.insuredValue?.toString() || ''),
        premium: formatCurrencyInput(property.premium?.toString() || ''),
        excess: formatCurrencyInput(property.excess?.toString() || ''),
        powerSurgeCoverValue: formatCurrencyInput(property.powerSurgeCoverValue?.toString() || ''),
        accidentalDamageCoverValue: formatCurrencyInput(property.accidentalDamageCoverValue?.toString() || ''),
      });
      
      // Also ensure propertyFormData has the numeric values
      setPropertyFormData(prev => ({
        ...prev,
        insuredValue: property.insuredValue || 0,
        premium: property.premium || '',
        excess: property.excess || 0,
        powerSurgeCoverValue: property.powerSurgeCoverValue || 0,
        accidentalDamageCoverValue: property.accidentalDamageCoverValue || 0,
      }));
    }
  }, [property, isEditMode, isViewMode, isVisible]);

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>, field: 'wallMaterials' | 'roofMaterials' | 'floorMaterials') => {
  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
  
  setPropertyFormData(prev => ({
    ...prev,
    [field]: selectedOptions,
  }));
  
  setPropertyErrors(prev => ({ ...prev, [field]: '' }));
};

  // Handle currency changes
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: 'insuredValue' | 'premium' | 'excess') => {
    const { value } = e.target;
    
    const formattedValue = formatCurrencyInput(value);
    const numericValue = parseCurrencyInput(value);
    
    setPropertyFormData(prev => ({
      ...prev,
      [fieldName]: numericValue,
    }));
    
    setCurrencyDisplay(prev => ({
      ...prev,
      [fieldName]: formattedValue,
    }));
    
    setPropertyErrors(prev => ({ ...prev, [fieldName]: '' }));
  };

  // Blur handlers for final formatting
  const handleCurrencyBlur = (field: 'insuredValue' | 'premium' | 'excess') => {
    const currentValue = currencyDisplay[field];
    if (currentValue) {
      const numericValue = parseCurrencyInput(currentValue);
      if (numericValue > 0) {
        const formatted = formatCurrencyInput(numericValue.toFixed(2));
        setCurrencyDisplay(prev => ({ ...prev, [field]: formatted }));
      }
    }
  };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      const isCheckbox = type === 'checkbox';
      
      if (isCheckbox) {
        const checked = (e.target as HTMLInputElement).checked;
        console.log(`✅ Checkbox ${name} changed to:`, checked);
        
        setPropertyFormData(prev => ({
          ...prev,
          [name]: checked,
        }));
      } else {
        // Special handling for cover value fields
        if (name === 'powerSurgeCoverValue' || name === 'accidentalDamageCoverValue') {
          const numericValue = parseFloat(value) || 0;
          setPropertyFormData(prev => ({
            ...prev,
            [name]: numericValue,
          }));
        } else {
          setPropertyFormData(prev => ({
            ...prev,
            [name]: value,
          }));
        }
        setPropertyErrors(prev => ({ ...prev, [name]: '' }));
      }
    };

    const fetchClientAddress = async (clientId: number) => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const endpoint = `${API_URL}/api/addresses/client/${clientId}`;
        console.log(`🔍 Fetching from endpoint: ${endpoint}`);
        
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`Server returned ${response.status}: ${errorText}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const errorText = await response.text();
          console.error('Non-JSON response:', errorText);
          throw new Error('Received non-JSON response from server');
        }

        const addressData = await response.json();
        console.log('✅ Address data received:', addressData);

        if (!addressData) {
          throw new Error('No address data returned for this client');
        }

        return addressData;

      } catch (error) {
        console.error('Error fetching address data:', error);
        throw error;
      }
    };

  const handleFillFromClient = async () => {
    try {
      console.log('🔄 Starting Fill from Client for clientId:', clientId);
      setSaveMessage('Fetching address...');

      const addressData = await fetchClientAddress(clientId);
      const mappedData = mapAddressData(addressData);

      console.log('🗺️ Final mapped address data for form:', mappedData);

      // Update the form with the correctly mapped data
      setPropertyFormData(prev => ({
        ...prev,
        ...mappedData,
      }));

      // Clear any previous errors
      setPropertyErrors(prev => ({
        ...prev,
        addressLine1: '',
        suburb: '',
        city: '',
        postalCode: '',
        region: '',
        country: '',
        server: '',
      }));

      setSaveMessage('✓ Address filled successfully from client data!');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);

    } catch (error) {
      console.error('❌ Error in handleFillFromClient:', error);
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'Unknown error occurred';
          
      setSaveMessage(`❌ Failed to fetch address: ${errorMessage}`);
      
      setTimeout(() => {
        setSaveMessage('');
      }, 5000);
    }
  };

  const validateForm = () => {
    const newErrors: PropertyErrors = {
      addressLine1: !propertyFormData.addressLine1 ? 'Address Line 1 is required' : '',
      suburb: !propertyFormData.suburb ? 'Suburb is required' : '',
      city: !propertyFormData.city ? 'City/Town is required' : '',
      postalCode: !propertyFormData.postalCode ? 'Postal Code is required' : '',
      region: !propertyFormData.region ? 'Region is required' : '',
      country: !propertyFormData.country ? 'Country is required' : '',
      propertyType: !propertyFormData.propertyType ? 'Property Type is required' : '',
      residenceType: !propertyFormData.residenceType ? 'Residence Type is required' : '',
      buildingAge: !propertyFormData.buildingAge ? 'Building Age is required' : '',
      situatedAt: !propertyFormData.situatedAt ? 'Situated At is required' : '',
      coverType: !propertyFormData.coverType ? 'Cover Type is required' : '',
      insuredValue: !propertyFormData.insuredValue ? 'Insured Value is required' : '',
      server: '',
      wallMaterials: '',
      roofMaterials: '',
      floorMaterials: '',
      otherSurroundingAreas: '',
      premium: '',
      excess: '',
      noClaimBonus: '',
    };
    
    setPropertyErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = (e: React.FormEvent) => {
  e.preventDefault();
  
  if (isSubmitting) return;
  
  if (!validateForm()) return;
  
  setIsSubmitting(true);
  setSaveMessage('Adding property...');

  // Debug log to see what's in propertyFormData
  console.log('🔍 propertyFormData before save:', {
    hasPowerSurgeCover: propertyFormData.hasPowerSurgeCover,
    powerSurgeCoverValue: propertyFormData.powerSurgeCoverValue,
    hasAccidentalDamageCover: propertyFormData.hasAccidentalDamageCover,
    accidentalDamageCoverValue: propertyFormData.accidentalDamageCoverValue,
  });

  // Just prepare the data
  const propertyToSave: Property = {
  ...propertyFormData,
  id: property?.id || `temp-property-${Date.now()}-${Math.random().toString(36).slice(2,10)}`,
  insuredValue: propertyFormData.insuredValue || 0,
  premium: propertyFormData.premium || '',
  excess: propertyFormData.excess || 0,
  
  // Ensure arrays are properly set
  wallMaterials: propertyFormData.wallMaterials || [],
  roofMaterials: propertyFormData.roofMaterials || [],
  floorMaterials: propertyFormData.floorMaterials || [],
  otherSurroundingAreas: propertyFormData.otherSurroundingAreas || [],
  
  // CRITICAL: Explicitly set thatch fields
  hasThatchRoof: propertyFormData.hasThatchRoof === true,
  thatchLessThan5m: propertyFormData.thatchLessThan5m === true,
  thatchSabsConductor: propertyFormData.thatchSabsConductor === true,
  thatchTreated: propertyFormData.thatchTreated === true,
  
  // Cover fields
  hasPowerSurgeCover: propertyFormData.hasPowerSurgeCover === true,
  powerSurgeCoverValue: propertyFormData.powerSurgeCoverValue || 0,
  hasAccidentalDamageCover: propertyFormData.hasAccidentalDamageCover === true,
  accidentalDamageCoverValue: propertyFormData.accidentalDamageCoverValue || 0,
  
  // All other boolean fields
  hasSwimmingPool: propertyFormData.hasSwimmingPool === true,
  hasMotorizedGate: propertyFormData.hasMotorizedGate === true,
  isUnoccupiedMoreThan60Days: propertyFormData.isUnoccupiedMoreThan60Days === true,
  isCommune: propertyFormData.isCommune === true,
  underConstruction: propertyFormData.underConstruction === true,
  builtOnSteepSlope: propertyFormData.builtOnSteepSlope === true,
  hasExcessiveRetainingWalls: propertyFormData.hasExcessiveRetainingWalls === true,
  nearWaterBody: propertyFormData.nearWaterBody === true,
  hasRisingDamp: propertyFormData.hasRisingDamp === true,
  hasWallCracks: propertyFormData.hasWallCracks === true,
  hasSubsidenceHistory: propertyFormData.hasSubsidenceHistory === true,
  hasOutbuilding: propertyFormData.hasOutbuilding === true,
  hasPerimeterFence: propertyFormData.hasPerimeterFence === true,
  hasElectricFence: propertyFormData.hasElectricFence === true,
  
  isEditing: false,
} as Property;

  console.log('📦 Passing property to parent (no backend save here):', {
    ...propertyToSave,
    // Log the specific fields we're concerned about
    coverFields: {
      hasPowerSurgeCover: propertyToSave.hasPowerSurgeCover,
      powerSurgeCoverValue: propertyToSave.powerSurgeCoverValue,
      hasAccidentalDamageCover: propertyToSave.hasAccidentalDamageCover,
      accidentalDamageCoverValue: propertyToSave.accidentalDamageCoverValue,
    }
  });

  // Call the parent's onSave — that's all!
  onSave(propertyToSave);

  setSaveMessage('Property added successfully!');
  
  // Close after short delay so user sees message
  setTimeout(() => {
    setIsSubmitting(false);
    handleClose();
  }, 1200);
};

  const handleClose = () => {
    console.log('🔒 Property Modal closing, resetting form');
    setPropertyFormData(getInitialFormData());
    setCurrencyDisplay({
      insuredValue: '',
      premium: '',
      excess: '',
      powerSurgeCoverValue: '',
      accidentalDamageCoverValue: '',
    });
    setPropertyErrors({
      addressLine1: '',
      suburb: '',
      city: '',
      postalCode: '',
      region: '',
      country: '',
      propertyType: '',
      residenceType: '',
      buildingAge: '',
      situatedAt: '',
      coverType: '',
      insuredValue: '',
      server: '',
      premium: '',
      excess: '',
      noClaimBonus: '',
      wallMaterials: '',
      roofMaterials: '',
      floorMaterials: '',
      otherSurroundingAreas: '',
    });
    setIsSubmitting(false);
    setSaveMessage('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">
          {isViewMode ? 'View Property Details' : 
          isEditMode ? 'Edit Property Details' : 'Add Property'}
        </h3>
        
        {saveMessage && (
          <div className={`${saveMessage.includes('❌') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'} border px-4 py-3 rounded relative mb-4`}>
            {saveMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mb-6">
            {isCreateMode && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleFillFromClient}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
                  disabled={isSubmitting}
                >
                  Fill from Client
                </button>
              </div>
            )}

            <div className="flex space-x-4">
              <div className="flex-1">
                <label htmlFor="addressLine1" className="block text-gray-700 font-bold mb-2 text-sm">
                  Address Line 1 *
                </label>
                <input
                  type="text"
                  id="addressLine1"
                  name="addressLine1"
                  value={propertyFormData.addressLine1}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="123 Main Street"
                  disabled={isViewMode}
                />
                {propertyErrors.addressLine1 && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.addressLine1}</p>
                )}
              </div>
              <div className="flex-1">
                <label htmlFor="addressLine2" className="block text-gray-700 font-bold mb-2 text-sm">
                  Address Line 2
                </label>
                <input
                  type="text"
                  id="addressLine2"
                  name="addressLine2"
                  value={propertyFormData.addressLine2}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="Apartment 4B"
                  disabled={isViewMode}
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <div className="flex-1">
                <label htmlFor="suburb" className="block text-gray-700 font-bold mb-2 text-sm">
                  Suburb *
                </label>
                <input
                  type="text"
                  id="suburb"
                  name="suburb"
                  value={propertyFormData.suburb}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="Leisure Bay"
                  disabled={isViewMode}
                />
                {propertyErrors.suburb && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.suburb}</p>
                )}
              </div>
              <div className="flex-1">
                <label htmlFor="city" className="block text-gray-700 font-bold mb-2 text-sm">
                  City/Town *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={propertyFormData.city}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="Cape Town"
                  disabled={isViewMode}
                />
                {propertyErrors.city && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.city}</p>
                )}
              </div>
            </div>

            <div className="flex space-x-4">
              <div className="flex-1">
                <label htmlFor="postalCode" className="block text-gray-700 font-bold mb-2 text-sm">
                  Postal Code *
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={propertyFormData.postalCode}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="8001"
                  disabled={isViewMode}
                />
                {propertyErrors.postalCode && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.postalCode}</p>
                )}
              </div>
              <div className="flex-1">
                <label htmlFor="region" className="block text-gray-700 font-bold mb-2 text-sm">
                  Region *
                </label>
                <input
                  type="text"
                  id="region"
                  name="region"
                  value={propertyFormData.region}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="Western Cape"
                  disabled={isViewMode}
                />
                {propertyErrors.region && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.region}</p>
                )}
              </div>
            </div>

            <div className="flex space-x-4">
              <div className="flex-1">
                <label htmlFor="country" className="block text-gray-700 font-bold mb-2 text-sm">
                  Country *
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={propertyFormData.country}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  placeholder="South Africa"
                  disabled={isViewMode}
                />
                {propertyErrors.country && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.country}</p>
                )}
              </div>
              <div className="flex-1">
                <label htmlFor="propertyType" className="block text-gray-700 font-bold mb-2 text-sm">
                  Property Type *
                </label>
                <select
                  id="propertyType"
                  name="propertyType"
                  value={propertyFormData.propertyType}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="" disabled>Select property type</option>
                  {propertyTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {propertyErrors.propertyType && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.propertyType}</p>
                )}
              </div>
            </div>

            <div className="flex space-x-4">
              <div className="flex-1">
                <label htmlFor="residenceType" className="block text-gray-700 font-bold mb-2 text-sm">
                  Residence Type *
                </label>
                <select
                  id="residenceType"
                  name="residenceType"
                  value={propertyFormData.residenceType}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="" disabled>Select residence type</option>
                  {residenceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {propertyErrors.residenceType && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.residenceType}</p>
                )}
              </div>
              <div className="flex-1">
                <label htmlFor="buildingAge" className="block text-gray-700 font-bold mb-2 text-sm">
                  Building Age
                </label>
                <select
                  id="buildingAge"
                  name="buildingAge"
                  value={propertyFormData.buildingAge}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="" disabled>Select building age</option>
                  {buildingAgeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {propertyErrors.buildingAge && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.buildingAge}</p>
                )}
              </div>
            </div>

            <div className="flex space-x-4">
              <div className="flex-1">
                <label htmlFor="situatedAt" className="block text-gray-700 font-bold mb-2 text-sm">
                  Situated At
                </label>
                <select
                  id="situatedAt"
                  name="situatedAt"
                  value={propertyFormData.situatedAt}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="" disabled>Select location type</option>
                  {situatedAtOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {propertyErrors.situatedAt && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.situatedAt}</p>
                )}
              </div>
              <div className="flex-1">
                <label htmlFor="coverType" className="block text-gray-700 font-bold mb-2 text-sm">
                  Cover Type *
                </label>
                <select
                  id="coverType"
                  name="coverType"
                  value={propertyFormData.coverType}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="" disabled>Select cover type</option>
                  {coverTypeOptions.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {propertyErrors.coverType && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.coverType}</p>
                )}
              </div>
            </div>

            {/* Insured Value Field with Currency Formatting */}
            <div className="flex space-x-4">
              <div className="flex-1">
                <label htmlFor="insuredValue" className="block text-gray-700 font-bold mb-2 text-sm">
                  Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">R</span>
                  </div>
                  <input
                    type="text"
                    id="insuredValue"
                    name="insuredValue"
                    value={currencyDisplay.insuredValue}
                    onChange={(e) => handleCurrencyChange(e, 'insuredValue')}
                    onBlur={() => handleCurrencyBlur('insuredValue')}
                    className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    placeholder="0.00"
                    disabled={isViewMode}
                  />
                </div>
                {propertyErrors.insuredValue && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.insuredValue}</p>
                )}
              </div>

              {/* No Claim Bonus Field */}
              <div className="flex-1">
                <label htmlFor="noClaimBonus" className="block text-gray-700 font-bold mb-2 text-sm">
                  No Claim Bonus
                </label>
                <select
                  id="noClaimBonus"
                  name="noClaimBonus"
                  value={propertyFormData.noClaimBonus || ''}
                  onChange={handleInputChange}
                  className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                  disabled={isViewMode}
                >
                  <option value="" disabled>Select no claim bonus</option>
                  {noClaimBonusOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {propertyErrors.noClaimBonus && (
                  <p className="text-red-500 text-xs mt-1">{propertyErrors.noClaimBonus}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-gray-700 font-bold mb-2 text-sm">Additional Property Details</h4>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasSwimmingPool"
                    checked={propertyFormData.hasSwimmingPool || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Has swimming pool
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasMotorizedGate"
                    checked={propertyFormData.hasMotorizedGate || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Has motorized gate
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="isUnoccupiedMoreThan60Days"
                    checked={propertyFormData.isUnoccupiedMoreThan60Days || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Is the property unoccupied for more than 60 consecutive days per annum
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="isCommune"
                    checked={propertyFormData.isCommune || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Is commune
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="underConstruction"
                    checked={propertyFormData.underConstruction || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Under construction
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="builtOnSteepSlope"
                    checked={propertyFormData.builtOnSteepSlope || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Built on steep slope
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasExcessiveRetainingWalls"
                    checked={propertyFormData.hasExcessiveRetainingWalls || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Has excessive use of retaining walls
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="nearWaterBody"
                    checked={propertyFormData.nearWaterBody || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Less than 100 m from Sea, Lagoon, River, Natural Stream, Dam or Swamp
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasRisingDamp"
                    checked={propertyFormData.hasRisingDamp || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Has visible signs of rising damp
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasWallCracks"
                    checked={propertyFormData.hasWallCracks || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Has visible signs of cracks in the wall
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasSubsidenceHistory"
                    checked={propertyFormData.hasSubsidenceHistory || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Has history of subsidence or landslip in the area
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasOutbuilding"
                    checked={propertyFormData.hasOutbuilding || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Has outbuilding
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasPerimeterFence"
                    checked={propertyFormData.hasPerimeterFence || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Property surrounded by perimeter fence at least 1.8m high
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasElectricFence"
                    checked={propertyFormData.hasElectricFence || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Has electric fence on perimeter
                </label>

                <div className="space-y-2">
                {/* Thatch Roof Checkbox - Main Trigger */}
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    name="hasThatchRoof"
                    checked={propertyFormData.hasThatchRoof || false}
                    onChange={handleInputChange}
                    className="mr-2"
                    disabled={isViewMode}
                  />
                  Thatch roof present
                </label>
                
                {/* Conditional Dropdown for Thatch Roof Details */}
                {propertyFormData.hasThatchRoof && (
                  <div className="ml-6 mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md space-y-2">
                    <p className="text-xs font-medium text-blue-800 mb-2">
                      Additional Thatch Roof Details:
                    </p>
                    
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        name="thatchLessThan5m"
                        checked={propertyFormData.thatchLessThan5m || false}
                        onChange={handleInputChange}
                        className="mr-2"
                        disabled={isViewMode}
                      />
                      Less than 5m from main dwelling
                    </label>
                    
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        name="thatchSabsConductor"
                        checked={propertyFormData.thatchSabsConductor || false}
                        onChange={handleInputChange}
                        className="mr-2"
                        disabled={isViewMode}
                      />
                      SABS lightning conductor present
                    </label>
                    
                    <label className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        name="thatchTreated"
                        checked={propertyFormData.thatchTreated || false}
                        onChange={handleInputChange}
                        className="mr-2"
                        disabled={isViewMode}
                      />
                      Thatch treated
                    </label>
                  </div>
                )}
              </div>

                {/* Accidental Damage Cover with Conditional Input Field */}
                <div className="space-y-2">
                  {/* Main Accidental Damage Checkbox */}
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      name="hasAccidentalDamageCover"
                      checked={propertyFormData.hasAccidentalDamageCover || false}
                      onChange={handleInputChange}
                      className="mr-2"
                      disabled={isViewMode}
                    />
                    Accidental damage cover
                  </label>
                  
                  {/* Conditional Input Field for Accidental Damage Value */}
                  {propertyFormData.hasAccidentalDamageCover && (
                    <div className="ml-6 mt-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Accidental Damage Cover Value
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm">R</span>
                        </div>
                        <input
                          type="text"
                          name="accidentalDamageCoverValue"
                          value={formatCurrencyInput(propertyFormData.accidentalDamageCoverValue?.toString() || '')}
                          onChange={(e) => {
                            // Format the input as currency
                            const formattedValue = formatCurrencyInput(e.target.value);
                            const numericValue = parseCurrencyInput(e.target.value);
                            
                            setPropertyFormData(prev => ({
                              ...prev,
                              accidentalDamageCoverValue: numericValue,
                            }));
                            
                            // Update currency display
                            setCurrencyDisplay(prev => ({
                              ...prev,
                              accidentalDamageCoverValue: formattedValue,
                            }));
                          }}
                          onBlur={() => {
                            const currentValue = propertyFormData.accidentalDamageCoverValue;
                            if (currentValue && currentValue > 0) {
                              const formatted = formatCurrencyInput(currentValue.toFixed(2));
                              setCurrencyDisplay(prev => ({
                                ...prev,
                                accidentalDamageCoverValue: formatted,
                              }));
                            }
                          }}
                          className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                          placeholder="0.00"
                          disabled={isViewMode}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the accidental damage cover amount
                      </p>
                    </div>
                  )}
                </div>

                {/* Power Surge Cover with Conditional Input Field */}
                <div className="space-y-2">
                  {/* Main Power Surge Checkbox */}
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      name="hasPowerSurgeCover"
                      checked={propertyFormData.hasPowerSurgeCover || false}
                      onChange={handleInputChange}
                      className="mr-2"
                      disabled={isViewMode}
                    />
                    Power surge cover
                  </label>
                  
                  {/* Conditional Input Field for Power Surge Value */}
                  {propertyFormData.hasPowerSurgeCover && (
                    <div className="ml-6 mt-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Power Surge Cover Value
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm">R</span>
                        </div>
                        <input
                          type="text"
                          name="powerSurgeCoverValue"
                          value={formatCurrencyInput(propertyFormData.powerSurgeCoverValue?.toString() || '')}
                          onChange={(e) => {
                            // Format the input as currency
                            const formattedValue = formatCurrencyInput(e.target.value);
                            const numericValue = parseCurrencyInput(e.target.value);
                            
                            setPropertyFormData(prev => ({
                              ...prev,
                              powerSurgeCoverValue: numericValue,
                            }));
                            
                            // Update currency display if needed
                            setCurrencyDisplay(prev => ({
                              ...prev,
                              powerSurgeCoverValue: formattedValue,
                            }));
                          }}
                          onBlur={() => {
                            const currentValue = propertyFormData.powerSurgeCoverValue;
                            if (currentValue && currentValue > 0) {
                              const formatted = formatCurrencyInput(currentValue.toFixed(2));
                              setCurrencyDisplay(prev => ({
                                ...prev,
                                powerSurgeCoverValue: formatted,
                              }));
                            }
                          }}
                          className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                          placeholder="0.00"
                          disabled={isViewMode}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the power surge cover amount
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-gray-700 font-bold mb-2 text-sm">Construction and Surroundings</h4>
              <div className="space-y-6">
                
                {/* Wall Materials - Checkbox Group */}
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Wall Materials
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {wallMaterialsOptions.map(option => {
                      const currentWallMaterials = propertyFormData.wallMaterials || [];
                      
                      // Clean both the option and the saved items for comparison
                      const cleanedOption = cleanMaterialArray([option])[0] || option;
                      const isChecked = currentWallMaterials.some(item => {
                        const cleanedItem = cleanMaterialArray([item])[0] || item;
                        return cleanedItem === cleanedOption;
                      });
                      
                      return (
                        <label key={option} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              handleCheckboxGroupChange('wallMaterials', option, e.target.checked);
                            }}
                            className="mr-2"
                            disabled={isViewMode}
                          />
                          {option}
                          {isChecked && <span className="ml-2 text-xs text-green-600 font-bold">✓ CHECKED</span>}
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    {propertyFormData.wallMaterials && propertyFormData.wallMaterials.length > 0 ? (
                      <p className="text-xs text-green-600">
                        Selected: {propertyFormData.wallMaterials.join(', ')}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">No selections</p>
                    )}

                    <button
                      type="button"
                      onClick={() => clearAllSelections('wallMaterials')}
                      className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-300"
                      disabled={isViewMode || (propertyFormData.wallMaterials?.length || 0) === 0}
                    >
                      Clear All
                    </button>

                  </div>
                  {propertyErrors.wallMaterials && (
                    <p className="text-red-500 text-xs mt-1">{propertyErrors.wallMaterials}</p>
                  )}
                </div>

                {/* Roof Materials - Checkbox Group */}
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Roof Materials
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {roofMaterialsOptions.map(option => (
                      <label key={option} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={(propertyFormData.roofMaterials || []).includes(option)}
                          onChange={(e) => handleCheckboxGroupChange('roofMaterials', option, e.target.checked)}
                          className="mr-2"
                          disabled={isViewMode}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    {propertyFormData.roofMaterials && propertyFormData.roofMaterials.length > 0 ? (
                      <p className="text-xs text-green-600">
                        Selected: {propertyFormData.roofMaterials.join(', ')}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">No selections</p>
                    )}
                    <button
                      type="button"
                      onClick={() => clearAllSelections('roofMaterials')}
                      className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-300"
                      disabled={isViewMode || (propertyFormData.roofMaterials?.length || 0) === 0}
                    >
                      Clear All
                    </button>
                  </div>
                  {propertyErrors.roofMaterials && (
                    <p className="text-red-500 text-xs mt-1">{propertyErrors.roofMaterials}</p>
                  )}
                </div>

                {/* Floor Materials - Checkbox Group */}
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Floor Materials
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {floorMaterialsOptions.map(option => (
                      <label key={option} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={(propertyFormData.floorMaterials || []).includes(option)}
                          onChange={(e) => handleCheckboxGroupChange('floorMaterials', option, e.target.checked)}
                          className="mr-2"
                          disabled={isViewMode}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    {propertyFormData.floorMaterials && propertyFormData.floorMaterials.length > 0 ? (
                      <p className="text-xs text-green-600">
                        Selected: {propertyFormData.floorMaterials.join(', ')}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">No selections</p>
                    )}
                    <button
                      type="button"
                      onClick={() => clearAllSelections('floorMaterials')}
                      className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-300"
                      disabled={isViewMode || (propertyFormData.floorMaterials?.length || 0) === 0}
                    >
                      Clear All
                    </button>
                  </div>
                  {propertyErrors.floorMaterials && (
                    <p className="text-red-500 text-xs mt-1">{propertyErrors.floorMaterials}</p>
                  )}
                </div>

                {/* Other Surrounding Areas - Checkbox Group */}
                <div>
                  <label className="block text-gray-700 font-bold mb-2 text-sm">
                    Other Surrounding Areas
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {surroundingAreasOptions.map(option => (
                      <label key={option} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={(propertyFormData.otherSurroundingAreas || []).includes(option)}
                          onChange={(e) => handleCheckboxGroupChange('otherSurroundingAreas', option, e.target.checked)}
                          className="mr-2"
                          disabled={isViewMode}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    {propertyFormData.otherSurroundingAreas && propertyFormData.otherSurroundingAreas.length > 0 ? (
                      <p className="text-xs text-green-600">
                        Selected: {propertyFormData.otherSurroundingAreas.join(', ')}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">No selections</p>
                    )}
                    <button
                      type="button"
                      onClick={() => clearAllSelections('otherSurroundingAreas')}
                      className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-300"
                      disabled={isViewMode || (propertyFormData.otherSurroundingAreas?.length || 0) === 0}
                    >
                      Clear All
                    </button>
                  </div>
                  {propertyErrors.otherSurroundingAreas && (
                    <p className="text-red-500 text-xs mt-1">{propertyErrors.otherSurroundingAreas}</p>
                  )}
                </div>

              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-gray-700 font-bold mb-2 text-sm">Insurance Details</h4>
              <div className="space-y-4">
                <div className="flex space-x-4">
                  {/* Premium Field with Currency Formatting */}
                  <div className="flex-1">
                    <label htmlFor="premium" className="block text-gray-700 font-bold mb-2 text-sm">
                      Premium
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        type="text"
                        id="premium"
                        name="premium"
                        value={currencyDisplay.premium}
                        onChange={(e) => handleCurrencyChange(e, 'premium')}
                        onBlur={() => handleCurrencyBlur('premium')}
                        className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        placeholder="0.00"
                        disabled={isViewMode}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Type any amount: "120,000.34" or "500.5" or "1,000,000"
                    </p>
                  </div>

                  {/* Excess Field with Currency Formatting */}
                  <div className="flex-1">
                    <label htmlFor="excess" className="block text-gray-700 font-bold mb-2 text-sm">
                      Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        type="text"
                        id="excess"
                        name="excess"
                        value={currencyDisplay.excess}
                        onChange={(e) => handleCurrencyChange(e, 'excess')}
                        onBlur={() => handleCurrencyBlur('excess')}
                        className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        placeholder="0.00"
                        disabled={isViewMode}
                      />
                    </div>
                    {propertyErrors.excess && (
                      <p className="text-red-500 text-xs mt-1">{propertyErrors.excess}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
              disabled={isSubmitting}
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>

            {/* Add Edit button for view mode */}
            {isViewMode && onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded text-sm"
              >
                Edit Property
              </button>
            )}

            {!isViewMode && (
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? (isEditMode ? 'Updating...' : 'Submitting...') : 
              isEditMode ? 'Update Property' : 'Submit Property'}
            </button>
          )}
            
          </div>
        </form>
      </div>
    </div>
  );
};

export const EditablePropertyCard: React.FC<EditablePropertyCardProps> = ({ 
  property, 
  index, 
  onSave, 
  onCancel, 
  onEdit,
  cardNumber,
  cardTitle = 'Property',
  showCardNumber = true
  }) => {
    const [editForm, setEditForm] = useState(() => {
    // Create a comprehensive initialization function
    const initialEditForm = {
      // Address fields
      addressLine1: property.addressLine1 || '',
      addressLine2: property.addressLine2 || '',
      suburb: property.suburb || '',
      city: property.city || '',
      postalCode: property.postalCode || '',
      region: property.region || '',
      country: property.country || '',
      
      // Property details
      propertyType: property.propertyType || '',
      residenceType: property.residenceType || '',
      buildingAge: property.buildingAge || '',
      situatedAt: property.situatedAt || '',
      coverType: property.coverType || '',
      insuredValue: property.insuredValue?.toString() || '',
      
      // Insurance details
      premium: property.premium || '',
      excess: property.excess?.toString() || '',
      noClaimBonus: property.noClaimBonus || '',
      
      // Array fields
      wallMaterials: Array.isArray(property.wallMaterials) 
        ? property.wallMaterials 
        : (property.wallMaterials ? [property.wallMaterials] : []),
      roofMaterials: Array.isArray(property.roofMaterials) 
        ? property.roofMaterials 
        : (property.roofMaterials ? [property.roofMaterials] : []),
      floorMaterials: Array.isArray(property.floorMaterials) 
        ? property.floorMaterials 
        : (property.floorMaterials ? [property.floorMaterials] : []),
      otherSurroundingAreas: Array.isArray(property.otherSurroundingAreas) 
        ? property.otherSurroundingAreas 
        : (property.otherSurroundingAreas ? [property.otherSurroundingAreas] : []),
      
      // CRITICAL FIX: Add ALL boolean fields with proper initialization
      // Thatch roof fields
      hasThatchRoof: property.hasThatchRoof || false,
      thatchLessThan5m: property.thatchLessThan5m || false,
      thatchSabsConductor: property.thatchSabsConductor || false,
      thatchTreated: property.thatchTreated || false,
      
      // Cover fields
      hasPowerSurgeCover: property.hasPowerSurgeCover || false,
      powerSurgeCoverValue: property.powerSurgeCoverValue?.toString() || '',
      hasAccidentalDamageCover: property.hasAccidentalDamageCover || false,
      accidentalDamageCoverValue: property.accidentalDamageCoverValue?.toString() || '',
      
      // Add ALL other boolean fields from Property interface
      hasSwimmingPool: property.hasSwimmingPool || false,
      hasMotorizedGate: property.hasMotorizedGate || false,
      isUnoccupiedMoreThan60Days: property.isUnoccupiedMoreThan60Days || false,
      isCommune: property.isCommune || false,
      underConstruction: property.underConstruction || false,
      builtOnSteepSlope: property.builtOnSteepSlope || false,
      hasExcessiveRetainingWalls: property.hasExcessiveRetainingWalls || false,
      nearWaterBody: property.nearWaterBody || false,
      hasRisingDamp: property.hasRisingDamp || false,
      hasWallCracks: property.hasWallCracks || false,
      hasSubsidenceHistory: property.hasSubsidenceHistory || false,
      hasOutbuilding: property.hasOutbuilding || false,
      hasPerimeterFence: property.hasPerimeterFence || false,
      hasElectricFence: property.hasElectricFence || false,
    };
    
    console.log('🔍 EditablePropertyCard editForm initialized:', {
      hasThatchRoof: initialEditForm.hasThatchRoof,
      thatchDetails: {
        lessThan5m: initialEditForm.thatchLessThan5m,
        sabsConductor: initialEditForm.thatchSabsConductor,
        treated: initialEditForm.thatchTreated
      },
      hasPowerSurgeCover: initialEditForm.hasPowerSurgeCover,
      powerSurgeCoverValue: initialEditForm.powerSurgeCoverValue,
      hasAccidentalDamageCover: initialEditForm.hasAccidentalDamageCover,
      accidentalDamageCoverValue: initialEditForm.accidentalDamageCoverValue
    });
    
    return initialEditForm;
  });

    // function to clear all checkboxes
    const clearSelections = (field: 'wallMaterials' | 'roofMaterials' | 'floorMaterials' | 'otherSurroundingAreas') => {
      setEditForm(prev => ({
        ...prev,
        [field]: []
      }));
    };

    // function
    const handleCheckboxChange = (
      field: 'wallMaterials' | 'roofMaterials' | 'floorMaterials' | 'otherSurroundingAreas',
      option: string,
      isChecked: boolean
    ) => {
      const currentArray = editForm[field] || [];
      
      if (isChecked) {
        // Add option if checked
        setEditForm(prev => ({
          ...prev,
          [field]: [...currentArray, option]
        }));
      } else {
        // Remove option if unchecked
        setEditForm(prev => ({
          ...prev,
          [field]: currentArray.filter(item => item !== option)
        }));
      }
    };

    const handleEditChange = (field: string, value: string | string[] | boolean | number) => {
      console.log(`✏️ Editing ${field}:`, value, 'type:', typeof value);
      
      // Handle boolean fields properly
      if (field === 'hasPowerSurgeCover' || field === 'hasAccidentalDamageCover' || 
          field === 'hasThatchRoof' || field === 'thatchLessThan5m' || 
          field === 'thatchSabsConductor' || field === 'thatchTreated') {
        // Ensure it's a boolean
        const boolValue = typeof value === 'string' ? value === 'true' : Boolean(value);
        setEditForm(prev => ({ ...prev, [field]: boolValue }));
        return;
      }
      
      // Handle numeric cover values
      if (field === 'powerSurgeCoverValue' || field === 'accidentalDamageCoverValue') {
        // If empty string, keep it as empty string (user is deleting)
        if (value === '') {
          setEditForm(prev => ({ ...prev, [field]: '' }));
        } 
        // If it's a string, try to parse it
        else if (typeof value === 'string') {
          const numValue = parseFloat(value) || 0;
          setEditForm(prev => ({ ...prev, [field]: numValue.toString() }));
        } 
        // If it's already a number
        else if (typeof value === 'number') {
          setEditForm(prev => ({ ...prev, [field]: value.toString() }));
        }
        return;
      }
      
      // Default handling for other fields
      setEditForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
      console.log('💾 Saving property with data:', {
        hasThatchRoof: editForm.hasThatchRoof,
        thatchDetails: {
          lessThan5m: editForm.thatchLessThan5m,
          sabsConductor: editForm.thatchSabsConductor,
          treated: editForm.thatchTreated
        },
        hasPowerSurgeCover: editForm.hasPowerSurgeCover,
        powerSurgeCoverValue: editForm.powerSurgeCoverValue,
        hasAccidentalDamageCover: editForm.hasAccidentalDamageCover,
        accidentalDamageCoverValue: editForm.accidentalDamageCoverValue
      });
      
      const updatedProperty: Property = {
        ...property,
        // Address fields
        addressLine1: editForm.addressLine1,
        addressLine2: editForm.addressLine2,
        suburb: editForm.suburb,
        city: editForm.city,
        postalCode: editForm.postalCode,
        region: editForm.region,
        country: editForm.country,
        
        // Property details
        propertyType: editForm.propertyType,
        residenceType: editForm.residenceType,
        buildingAge: editForm.buildingAge,
        situatedAt: editForm.situatedAt,
        coverType: editForm.coverType,
        insuredValue: parseFloat(editForm.insuredValue) || 0,
        
        // Insurance details
        premium: editForm.premium,
        excess: parseFloat(editForm.excess) || 0,
        noClaimBonus: editForm.noClaimBonus,
        
        // Array fields
        wallMaterials: editForm.wallMaterials || [],
        roofMaterials: editForm.roofMaterials || [],
        floorMaterials: editForm.floorMaterials || [],
        otherSurroundingAreas: editForm.otherSurroundingAreas || [],
        
        // Ensure all boolean fields are included
        hasThatchRoof: Boolean(editForm.hasThatchRoof),
        thatchLessThan5m: Boolean(editForm.thatchLessThan5m),
        thatchSabsConductor: Boolean(editForm.thatchSabsConductor),
        thatchTreated: Boolean(editForm.thatchTreated),

        hasPowerSurgeCover: Boolean(editForm.hasPowerSurgeCover),
        powerSurgeCoverValue: editForm.powerSurgeCoverValue === '' ? 0 : parseFloat(editForm.powerSurgeCoverValue) || 0,
        hasAccidentalDamageCover: Boolean(editForm.hasAccidentalDamageCover),
        accidentalDamageCoverValue: editForm.accidentalDamageCoverValue === '' ? 0 : parseFloat(editForm.accidentalDamageCoverValue) || 0,
        
        // Include ALL other boolean fields
        hasSwimmingPool: Boolean(editForm.hasSwimmingPool),
        hasMotorizedGate: Boolean(editForm.hasMotorizedGate),
        isUnoccupiedMoreThan60Days: Boolean(editForm.isUnoccupiedMoreThan60Days),
        isCommune: Boolean(editForm.isCommune),
        underConstruction: Boolean(editForm.underConstruction),
        builtOnSteepSlope: Boolean(editForm.builtOnSteepSlope),
        hasExcessiveRetainingWalls: Boolean(editForm.hasExcessiveRetainingWalls),
        nearWaterBody: Boolean(editForm.nearWaterBody),
        hasRisingDamp: Boolean(editForm.hasRisingDamp),
        hasWallCracks: Boolean(editForm.hasWallCracks),
        hasSubsidenceHistory: Boolean(editForm.hasSubsidenceHistory),
        hasOutbuilding: Boolean(editForm.hasOutbuilding),
        hasPerimeterFence: Boolean(editForm.hasPerimeterFence),
        hasElectricFence: Boolean(editForm.hasElectricFence),
        
        isEditing: false
      };
      
      console.log('✅ Final property to save:', updatedProperty);
      onSave(index, updatedProperty);
    };

    // Format currency for display
    const formatCurrency = (value: number | string): string => {
      if (!value) return '';
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      return isNaN(numValue) ? '' : `R ${numValue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Format address for display
    const formatAddress = (): string => {
      const addressParts = [
        property.addressLine1,
        property.addressLine2,
        property.suburb,
        property.city
      ].filter(part => part && part.trim() !== '');
      return addressParts.join(', ');
    };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-semibold text-blue-600">
            {showCardNumber ? `${cardTitle} #${cardNumber || (index + 1)}` : cardTitle}
          </h4>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit(index)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Edit
            </button>
          </div>
        </div>
        
        {property.isEditing ? (
          <div className="space-y-3">
            {/* Editable Form - Simplified version of your modal form */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-600 text-sm mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={editForm.addressLine1}
                  onChange={(e) => handleEditChange('addressLine1', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Suburb</label>
                <input
                  type="text"
                  value={editForm.suburb}
                  onChange={(e) => handleEditChange('suburb', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">City</label>
                <input
                  type="text"
                  value={editForm.city}
                  onChange={(e) => handleEditChange('city', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Property Type</label>
                <select
                  value={editForm.propertyType}
                  onChange={(e) => handleEditChange('propertyType', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select type</option>
                  {propertyTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Residence Type *</label>
                <select
                  value={editForm.residenceType}
                  onChange={(e) => handleEditChange('residenceType', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select residence type</option>
                  {residenceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Building Age</label>
                <select
                  value={editForm.buildingAge}
                  onChange={(e) => handleEditChange('buildingAge', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select building age</option>
                  {buildingAgeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
              <label className="block text-gray-600 text-sm mb-1">Situated At</label>
              <select
                value={editForm.situatedAt}
                onChange={(e) => handleEditChange('situatedAt', e.target.value)}
                className="w-full p-2 border rounded text-sm"
              >
                <option value="">Select location type</option>
                {situatedAtOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-1">Cover Type *</label>
              <select
                value={editForm.coverType}
                onChange={(e) => handleEditChange('coverType', e.target.value)}
                className="w-full p-2 border rounded text-sm"
              >
                <option value="">Select cover type</option>
                {coverTypeOptions.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Insured Value</label>
                <input
                  type="number"
                  value={editForm.insuredValue}
                  onChange={(e) => handleEditChange('insuredValue', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">No Claim Bonus</label>
                <select
                  value={editForm.noClaimBonus || ''}
                  onChange={(e) => handleEditChange('noClaimBonus', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select no claim bonus</option>
                  {noClaimBonusOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

             {/* Additional address fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-600 text-sm mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={editForm.addressLine2}
                    onChange={(e) => handleEditChange('addressLine2', e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 text-sm mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={editForm.postalCode}
                    onChange={(e) => handleEditChange('postalCode', e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 text-sm mb-1">Region</label>
                  <input
                    type="text"
                    value={editForm.region}
                    onChange={(e) => handleEditChange('region', e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 text-sm mb-1">Country</label>
                  <input
                    type="text"
                    value={editForm.country}
                    onChange={(e) => handleEditChange('country', e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                </div>
              </div>

            {/* Additional boolean checkboxes */}
            <div className="grid grid-cols-2 gap-2 p-2 bg-gray-50 rounded border">
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={editForm.hasSwimmingPool}
                  onChange={(e) => handleEditChange('hasSwimmingPool', e.target.checked)}
                  className="mr-1"
                />
                Has swimming pool
              </label>
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={editForm.hasMotorizedGate}
                  onChange={(e) => handleEditChange('hasMotorizedGate', e.target.checked)}
                  className="mr-1"
                />
                Has motorized gate
              </label>
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={editForm.hasElectricFence}
                  onChange={(e) => handleEditChange('hasElectricFence', e.target.checked)}
                  className="mr-1"
                />
                Has electric fence
              </label>
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={editForm.hasPerimeterFence}
                  onChange={(e) => handleEditChange('hasPerimeterFence', e.target.checked)}
                  className="mr-1"
                />
                Has perimeter fence
              </label>
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={editForm.isUnoccupiedMoreThan60Days}
                  onChange={(e) => handleEditChange('isUnoccupiedMoreThan60Days', e.target.checked)}
                  className="mr-1"
                />
                Unoccupied 60+ days
              </label>
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={editForm.isCommune}
                  onChange={(e) => handleEditChange('isCommune', e.target.checked)}
                  className="mr-1"
                />
                Is commune
              </label>
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={editForm.underConstruction}
                  onChange={(e) => handleEditChange('underConstruction', e.target.checked)}
                  className="mr-1"
                />
                Under construction
              </label>
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  checked={editForm.hasOutbuilding}
                  onChange={(e) => handleEditChange('hasOutbuilding', e.target.checked)}
                  className="mr-1"
                />
                Has outbuilding
              </label>
            </div>

            {/* Wall Materials Checkbox Group */}
            <div>
              <label className="block text-gray-600 text-sm mb-1">Wall Materials</label>
              <div className="grid grid-cols-2 gap-2">
                {wallMaterialsOptions.map(option => (
                  <label key={option} className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      checked={(editForm.wallMaterials || []).includes(option)}
                      onChange={(e) => handleCheckboxChange('wallMaterials', option, e.target.checked)}
                      className="mr-1"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {(editForm.wallMaterials && editForm.wallMaterials.length > 0) && (
                <p className="text-xs text-green-600 mt-1">
                  Selected: {editForm.wallMaterials.join(', ')}
                </p>
              )}
            </div>

            {/* Roof Materials Checkbox Group */}
            <div>
              <label className="block text-gray-600 text-sm mb-1">Roof Materials</label>
              <div className="grid grid-cols-2 gap-2">
                {roofMaterialsOptions.map(option => (
                  <label key={option} className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      checked={(editForm.roofMaterials || []).includes(option)}
                      onChange={(e) => handleCheckboxChange('roofMaterials', option, e.target.checked)}
                      className="mr-1"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {(editForm.roofMaterials && editForm.roofMaterials.length > 0) && (
                <p className="text-xs text-green-600 mt-1">
                  Selected: {editForm.roofMaterials.join(', ')}
                </p>
              )}
            </div>

            {/* Floor Materials Checkbox Group */}
            <div>
              <label className="block text-gray-600 text-sm mb-1">Floor Materials</label>
              <div className="grid grid-cols-2 gap-2">
                {floorMaterialsOptions.map(option => (
                  <label key={option} className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      checked={(editForm.floorMaterials || []).includes(option)}
                      onChange={(e) => handleCheckboxChange('floorMaterials', option, e.target.checked)}
                      className="mr-1"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {(editForm.floorMaterials && editForm.floorMaterials.length > 0) && (
                <p className="text-xs text-green-600 mt-1">
                  Selected: {editForm.floorMaterials.join(', ')}
                </p>
              )}
            </div>

            {/* Other Surrounding Areas Checkbox Group */}
            <div>
              <label className="block text-gray-600 text-sm mb-1">Surrounding Areas</label>
              <div className="grid grid-cols-2 gap-2">
                {surroundingAreasOptions.slice(0, 8).map(option => (
                  <label key={option} className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      checked={(editForm.otherSurroundingAreas || []).includes(option)}
                      onChange={(e) => handleCheckboxChange('otherSurroundingAreas', option, e.target.checked)}
                      className="mr-1"
                    />
                    {option}
                  </label>
                ))}
              </div>
              {(editForm.otherSurroundingAreas && editForm.otherSurroundingAreas.length > 0) && (
                <p className="text-xs text-green-600 mt-1">
                  {editForm.otherSurroundingAreas.length} areas selected
                </p>
              )}
            </div>

            <div className="p-2 bg-gray-50 rounded border border-gray-200">

              {/* THATCH ROOF SECTION */}
              <label className="flex items-center text-sm mb-2">
                <input
                  type="checkbox"
                  checked={editForm.hasThatchRoof || false}
                  onChange={(e) => handleEditChange('hasThatchRoof', e.target.checked)}  // FIXED: Pass boolean directly
                  className="mr-2"
                />
                <span className="font-medium">Thatch roof present</span>
              </label>

              {(editForm.hasThatchRoof || false) && (
                <div className="ml-4 space-y-2 mt-2">
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      checked={editForm.thatchLessThan5m || false}
                      onChange={(e) => handleEditChange('thatchLessThan5m', e.target.checked)}  // FIXED
                      className="mr-1"
                    />
                    Less than 5m from main dwelling
                  </label>
                  
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      checked={editForm.thatchSabsConductor || false}
                      onChange={(e) => handleEditChange('thatchSabsConductor', e.target.checked)}  // FIXED
                      className="mr-1"
                    />
                    SABS lightning conductor present
                  </label>
                  
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      checked={editForm.thatchTreated || false}
                      onChange={(e) => handleEditChange('thatchTreated', e.target.checked)}  // FIXED
                      className="mr-1"
                    />
                    Thatch treated
                  </label>
                </div>
              )}

            </div>

              
            <div className="p-2 bg-blue-50 rounded border border-blue-100">
              <label className="flex items-center text-sm mb-2">
                <input
                  type="checkbox"
                  checked={editForm.hasPowerSurgeCover || false}
                  onChange={(e) => handleEditChange('hasAccidentalDamageCover', e.target.checked)}
                  className="mr-2"
                />
                <span className="font-medium">Power surge cover</span>
              </label>
              
              {(editForm.hasPowerSurgeCover || false) && (
                <div className="ml-4 mt-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Cover Value (R)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-xs">R</span>
                    </div>
                    <input
                      type="text"
                      value={editForm.powerSurgeCoverValue?.toString() || ''}
                      onChange={(e) => handleEditChange('powerSurgeCoverValue', e.target.value)}
                      className="w-full p-2 pl-8 border rounded text-sm"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="p-2 bg-green-50 rounded border border-green-100">
              <label className="flex items-center text-sm mb-2">
                <input
                  type="checkbox"
                  checked={editForm.hasAccidentalDamageCover || false}
                  onChange={(e) => handleEditChange('hasAccidentalDamageCover', e.target.checked)}
                  className="mr-2"
                />
                <span className="font-medium">Accidental damage cover</span>
              </label>
              
              {(editForm.hasAccidentalDamageCover || false) && (
                <div className="ml-4 mt-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Cover Value (R)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-xs">R</span>
                    </div>
                    <input
                      type="text"
                      value={editForm.accidentalDamageCoverValue?.toString() || ''}
                      onChange={(e) => handleEditChange('accidentalDamageCoverValue', e.target.value)}
                      className="w-full p-2 pl-8 border rounded text-sm"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4">
              <h4 className="text-gray-700 font-bold mb-2 text-sm">Insurance Details</h4>
              <div className="space-y-4">
                <div className="flex space-x-4">
                  {/* Premium Field */}
                  <div className="flex-1">
                    <label className="block text-gray-600 text-sm mb-1">Premium</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-xs">R</span>
                      </div>
                      <input
                        type="text"
                        value={editForm.premium}
                        onChange={(e) => handleEditChange('premium', e.target.value)}
                        className="w-full p-2 pl-8 border rounded text-sm"
                        placeholder="0.00"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Type any amount: "120,000.34" or "500.5" or "1,000,000"
                    </p>
                  </div>

                  {/* Excess Field */}
                  <div className="flex-1">
                    <label className="block text-gray-600 text-sm mb-1">
                      Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-xs">R</span>
                      </div>
                      <input
                        type="text"
                        value={editForm.excess}
                        onChange={(e) => handleEditChange('excess', e.target.value)}
                        className="w-full p-2 pl-8 border rounded text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                onClick={handleSave}
                className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1 rounded"
              >
                Save
              </button>
              <button
                onClick={() => onCancel(index)}
                className="bg-gray-500 hover:bg-gray-600 text-white text-xs px-3 py-1 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {/* Display Mode */}
            <div className="flex justify-between">
              <span className="text-gray-600">Address:</span>
              <span className="font-medium text-right max-w-[150px]">
                {formatAddress() || 'No address provided'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Property Type:</span>
              <span className="font-medium">{property.propertyType || 'Not set'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Residence Type:</span>
              <span className="font-medium">{property.residenceType || 'Not set'}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Insured Amount:</span>
              <span className="font-medium text-green-600">
                {formatCurrency(property.insuredValue || 0) || 'Not set'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Premium:</span>
              <span className="font-medium text-purple-600">
                {property.premium ? `R ${property.premium}` : 'Not set'}
              </span>
            </div>

            {property.coverType && (
              <div className="flex justify-between">
                <span className="text-gray-600">Cover Type:</span>
                <span className="font-medium">{property.coverType}</span>
              </div>
            )}

            {/* THATCH ROOF DISPLAY */}
            {property.hasThatchRoof && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-amber-800">Thatch Roof:</span>
                  <span className="text-xs text-amber-600">✓ Present</span>
                </div>
                
                {(property.thatchLessThan5m || property.thatchSabsConductor || property.thatchTreated) && (
                  <div className="mt-1 space-y-1">
                    {property.thatchLessThan5m && (
                      <div className="text-xs text-amber-700">• Less than 5m from main dwelling</div>
                    )}
                    {property.thatchSabsConductor && (
                      <div className="text-xs text-amber-700">• SABS lightning conductor present</div>
                    )}
                    {property.thatchTreated && (
                      <div className="text-xs text-amber-700">• Thatch treated</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {property.hasPowerSurgeCover && (
              <div className="flex justify-between items-center p-2 bg-blue-50 border border-blue-200 rounded-md">
                <span className="text-gray-600 font-medium">Power Surge Cover:</span>
                <span className="font-medium text-blue-700">
                  {property.powerSurgeCoverValue && property.powerSurgeCoverValue > 0 
                    ? `R ${property.powerSurgeCoverValue.toLocaleString('en-ZA', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}`
                    : <span className="text-gray-500 text-xs">No value set</span>
                  }
                </span>
              </div>
            )}

            {property.hasAccidentalDamageCover && (
              <div className="flex justify-between items-center p-2 bg-green-50 border border-green-200 rounded-md">
                <span className="text-gray-600 font-medium">Accidental Damage Cover:</span>
                <span className="font-medium text-green-700">
                  {property.accidentalDamageCoverValue && property.accidentalDamageCoverValue > 0 
                    ? `R ${property.accidentalDamageCoverValue.toLocaleString('en-ZA', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}`
                    : <span className="text-gray-500 text-xs">No value set</span>
                  }
                </span>
              </div>
            )}
            
          </div>
        )}
      </div>
    </div>
  );
};

export default AddPropertyModal;